import { useMemo, useState } from 'react';

import {
  ValidationReport as Report,
  ValidationEntry,
  ValidationStatus,
} from '../../types';

import ExtractionWarnings from '../ExtractionWarnings';

import {
  Banner,
  BannerDetail,
  BannerTitle,
  DocumentLine,
  InferredMark,
  Label,
  Note,
  Root,
  Row,
  RowHead,
  Section,
  SectionTitle,
  StatusChip,
  StructuralFinding,
  ToggleButton,
  Value,
} from './styles';

interface Props {
  report: Report;
  className?: string;
}

const STATUS_LABEL: Record<ValidationStatus, string> = {
  match: 'Match',
  mismatch: 'Differs',
  not_in_document: 'Not in document',
  not_verified: 'Not verified',
};

/** Findings first, then everything the reader only skims. */
const STATUS_ORDER: Record<ValidationStatus, number> = {
  mismatch: 0,
  not_verified: 1,
  not_in_document: 2,
  match: 3,
};

const renderValue = (
  display: string | null,
  value: ValidationEntry['document_value'],
) => {
  if (display) return display;
  if (value === null || value === undefined) return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  return value;
};

const byInterest = (a: ValidationEntry, b: ValidationEntry) =>
  STATUS_ORDER[a.status] - STATUS_ORDER[b.status];

const EntryRows = ({ entries }: { entries: ValidationEntry[] }) => (
  <>
    {entries.map((entry) => (
      <div key={entry.field}>
        <Row>
          <Label>
            {entry.label}
            {entry.inferred && <InferredMark>inferred</InferredMark>}
          </Label>
          <Value $muted={entry.document_value === null}>
            {renderValue(entry.document_display, entry.document_value)}
          </Value>
          <Value $muted={entry.trade_value === null}>
            {renderValue(entry.trade_display, entry.trade_value)}
          </Value>
          <StatusChip $status={entry.status}>
            {STATUS_LABEL[entry.status]}
          </StatusChip>
        </Row>
        {entry.note && <Note>{entry.note}</Note>}
      </div>
    ))}
  </>
);

const EntrySection = ({
  title,
  entries,
}: {
  title: string;
  entries: ValidationEntry[];
}) => {
  const [showInformational, setShowInformational] = useState(false);

  const { material, informational } = useMemo(() => {
    // Calendars and conventions are rarely stated and often derived by us, so a difference
    // there is not a finding. Collapsed by default, or they bury the ones that are.
    const isMaterial = (entry: ValidationEntry) =>
      entry.severity !== 'informational';
    return {
      material: entries.filter(isMaterial).sort(byInterest),
      informational: entries.filter((e) => !isMaterial(e)).sort(byInterest),
    };
  }, [entries]);

  if (!entries.length) return null;

  return (
    <Section>
      <SectionTitle>{title}</SectionTitle>
      <RowHead>
        <div>Field</div>
        <div>In the document</div>
        <div>In the trade</div>
        <div />
      </RowHead>
      <EntryRows entries={material} />
      {informational.length > 0 && (
        <>
          {showInformational && <EntryRows entries={informational} />}
          <ToggleButton
            type="button"
            onClick={() => setShowInformational((shown) => !shown)}>
            {showInformational
              ? 'Hide secondary checks'
              : `Show ${informational.length} secondary check${
                  informational.length === 1 ? '' : 's'
                }`}
          </ToggleButton>
        </>
      )}
    </Section>
  );
};

const ValidationReport = ({ report, className }: Props) => {
  const { outcome, totals, has_unverified: hasUnverified } = report;

  const secondaryMismatched = totals.mismatched - totals.mismatched_material;

  const title = useMemo(() => {
    if (outcome === 'not_comparable') return 'Could not be compared';
    if (outcome === 'mismatch') {
      // Counting the secondary differences here would put a number in the headline that the
      // outcome does not act on - "2 differences found" above a trade that matches on all of them.
      return `${totals.mismatched_material} difference${
        totals.mismatched_material === 1 ? '' : 's'
      } found`;
    }
    // "Matches" on its own would overstate a report that could not check everything.
    return hasUnverified
      ? 'Matches, but not everything could be checked'
      : 'Matches the confirmation';
  }, [outcome, totals.mismatched_material, hasUnverified]);

  return (
    <Root className={className}>
      <Banner $outcome={outcome}>
        <BannerTitle $outcome={outcome}>{title}</BannerTitle>
        {report.comparable && (
          <BannerDetail>
            {[
              `${totals.checked} checked`,
              totals.matched && `${totals.matched} matched`,
              totals.mismatched_material &&
                `${totals.mismatched_material} differ`,
              secondaryMismatched &&
                `${secondaryMismatched} differ in secondary checks`,
              totals.not_in_document &&
                `${totals.not_in_document} not in document`,
              totals.not_verified && `${totals.not_verified} not verified`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </BannerDetail>
        )}
        <DocumentLine>
          {report.document.file_name} · {report.document.page_count} page
          {report.document.page_count === 1 ? '' : 's'}
          {report.document.confirmation_reference
            ? ` · ref ${report.document.confirmation_reference}`
            : ''}
        </DocumentLine>
      </Banner>

      <ExtractionWarnings warnings={report.warnings ?? []} />

      {report.structural_finding && (
        <StructuralFinding>{report.structural_finding}</StructuralFinding>
      )}

      <EntrySection title="Trade" entries={report.trade_summary} />

      {report.legs.map((leg) => (
        <EntrySection
          key={leg.leg_index}
          title={`Leg ${leg.leg_index + 1}${
            leg.leg_type ? ` — ${leg.leg_type}` : ''
          }`}
          entries={leg.fields}
        />
      ))}
    </Root>
  );
};

export default ValidationReport;
